import React, { useMemo, useState } from "react";
import "../css/Lp2App.css";
import {
  BootCondition,
  CreateListingRequest,
  ShoeType,
  bootloopClient,
  conditionLabel,
  shoeTypeLabel,
} from "../api/bootloopClient";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ListBoots: React.FC = () => {
  const [email, setEmail] = useState("");
  const [postcode, setPostcode] = useState("");
  const [sizeUk, setSizeUk] = useState<number>(3.5);
  const [shoeType, setShoeType] = useState<ShoeType>(1);
  const [condition, setCondition] = useState<BootCondition>(2);
  const [notes, setNotes] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const sizeHelp = useMemo(() => "Allowed ranges: 10–13.5 or 1–6.5", []);

  function validate(): string | null {
    const e = email.trim();
    if (!e) return "Email is required.";
    if (!emailRegex.test(e)) return "Please enter a valid email address.";

    const s = Number(sizeUk);
    const inKids = s >= 10 && s <= 13.5;
    const inYouth = s >= 1 && s <= 6.5;
    if (!inKids && !inYouth) return "Size must be between 10–13.5 or 1–6.5.";

    if (![0, 1, 2, 3, 4].includes(shoeType)) return "Invalid shoe type.";
    if (![0, 1, 2, 3, 4].includes(condition)) return "Invalid condition.";

    if (photoUrl.trim() && !/^https?:\/\/.+/i.test(photoUrl.trim()))
      return "Photo URL must start with http:// or https://";

    return null;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(null);

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    const payload: CreateListingRequest = {
      email: email.trim(),
      postcode: postcode.trim() || null,
      sizeUk: Number(sizeUk),
      shoeType,
      condition,
      notes: notes.trim() || null,
      photoUrl: photoUrl.trim() || null,
    };

    try {
      setBusy(true);
      const created = await bootloopClient.createListing(payload);
      setOk(`Listing created (ID ${created.listingId}).`);
      setNotes("");
      setPhotoUrl("");
    } catch (err: any) {
      setError(err?.message || "Failed to create listing.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lp2app">
      <h1 className="lp2app__title">List your boots</h1>
      <p className="lp2app__lead">Add a pair so other parents can find them.</p>

      {error && <div className="lp2app__alert">{error}</div>}
      {ok && <div className="lp2app__success">{ok}</div>}

      <form className="lp2app__card lp2app__form" onSubmit={submit}>
        <div className="lp2app__row2">
          <label className="lp2app__label">
            Email
            <input
              className="lp2app__input"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              placeholder="parent@example.com"
              autoComplete="email"
            />
          </label>

          <label className="lp2app__label">
            Postcode (optional)
            <input
              className="lp2app__input"
              value={postcode}
              onChange={(ev) => setPostcode(ev.target.value)}
              placeholder="CM1 3AB"
              autoComplete="postal-code"
            />
            <span className="lp2app__help">Used later for local-first matching.</span>
          </label>
        </div>

        <div className="lp2app__row2">
          <label className="lp2app__label">
            Size (UK)
            <input
              className="lp2app__input"
              type="number"
              step="0.5"
              value={sizeUk}
              onChange={(ev) => setSizeUk(Number(ev.target.value))}
            />
            <span className="lp2app__help">{sizeHelp}</span>
          </label>

          <label className="lp2app__label">
            Boot type
            <select
              className="lp2app__select"
              value={shoeType}
              onChange={(ev) => setShoeType(Number(ev.target.value) as ShoeType)}
            >
              {[0, 1, 2, 3, 4].map((v) => (
                <option key={v} value={v}>
                  {shoeTypeLabel(v as ShoeType)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="lp2app__row2">
          <label className="lp2app__label">
            Condition
            <select
              className="lp2app__select"
              value={condition}
              onChange={(ev) => setCondition(Number(ev.target.value) as BootCondition)}
            >
              {[0, 1, 2, 3, 4].map((v) => (
                <option key={v} value={v}>
                  {conditionLabel(v as BootCondition)}
                </option>
              ))}
            </select>
          </label>

          <label className="lp2app__label">
            Photo URL (optional)
            <input
              className="lp2app__input"
              value={photoUrl}
              onChange={(ev) => setPhotoUrl(ev.target.value)}
              placeholder="https://..."
            />
          </label>
        </div>

        <label className="lp2app__label">
          Notes (optional)
          <textarea
            className="lp2app__textarea"
            value={notes}
            onChange={(ev) => setNotes(ev.target.value)}
            placeholder="Studs good, worn half season, any scuffs, etc."
          />
        </label>

        <div className="lp2app__actions">
          <button className="lp2app__btnPrimary" type="submit" disabled={busy}>
            {busy ? "Saving..." : "Create listing"}
          </button>
          <span className="lp2app__help">
            Tip: short + honest notes reduce no-shows.
          </span>
        </div>
      </form>
    </div>
  );
};

export default ListBoots;
